import React, { useState, useEffect } from 'react';
import { Filter, Camera } from 'lucide-react';
import { galleryAPI } from '../services/api';

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredImages, setFilteredImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([
    { id: 'all', name: 'Alle', slug: 'all', image_count: 0 }
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
    fetchImages('all');
  }, []);

  const fetchCategories = async () => {
    try {
      const cats = await galleryAPI.getCategories();
      setCategories([
        { id: 'all', name: 'Alle', slug: 'all', image_count: cats.reduce((sum, c) => sum + c.image_count, 0) },
        ...cats
      ]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchImages = async (category) => {
    setLoading(true);
    try {
      const images = await galleryAPI.getImages(category);
      setFilteredImages(images);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categorySlug) => {
    setActiveCategory(categorySlug);
    fetchImages(categorySlug);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing-large bg-gray-50">
        <div className="photo-container text-center">
          <h1 className="hero-title mb-6">Meine Galerie</h1>
          <p className="subtitle max-w-2xl mx-auto">
            Entdecken Sie meine Fotografien aus verschiedenen Kategorien
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-50">
        <div className="photo-container">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Filter size={20} className="text-gray-600" />
            <span className="text-sm font-medium uppercase tracking-wider">Kategorie wählen</span>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-6 py-2 rounded-full font-medium text-sm uppercase tracking-wider transition-all ${
                  activeCategory === cat.slug
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.name} ({cat.image_count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-spacing">
        <div className="photo-container">
          {loading ? (
            <div className="text-center py-12">
              <Camera size={48} className="mx-auto mb-4 text-gray-400 animate-pulse" />
              <p className="text-gray-600">Bilder werden geladen...</p>
            </div>
          ) : filteredImages.length > 0 ? (
            <div className="photo-grid">
              {filteredImages.map((image, index) => (
                <div 
                  key={image.id} 
                  className="photo-card fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`} alt={image.title} />
                  <div className="photo-overlay">
                    <h3 className="font-heading text-xl font-semibold">{image.title}</h3>
                    <p className="text-sm mt-2 uppercase tracking-wider">{image.category}</p>
                  </div>
                  {/* Watermark */}
                  <div className="absolute bottom-4 right-4 text-white text-xs opacity-70 font-semibold">
                    © Franziska Daum
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Keine Bilder in dieser Kategorie verfügbar</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Gallery;
